import React, { useRef, useState } from 'react';
import { hasOwnProperty } from '@go-shared/lib/utils';
import BubbleInput, { BubbleInputProps } from './BubbleInput';
import BubbleButton from './BubbleButton';

import './BubbleHintSelect.scss';

interface BubbleHintInputProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BubbleInputProps['variant'];
  value?: string;
  options?: string[];
  block?: boolean;
  readOnly?: boolean;
  menuClassName?: string;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  subtitle?: string;
  keyExtractor?: (option: string) => string;
  labelExtractor?: (option: string) => string;
  onValue?: (value: string) => void;
}

export default function BubbleHintInput(props: BubbleHintInputProps) {
  const {
    value,
    options: propOptions,
    block,
    keyExtractor,
    labelExtractor,
    readOnly,
    onValue,
    name,
    menuClassName,
    variant,
    placeholder,
    disabled,
    subtitle,
    className: propClassName,
    style: propStyle,
    ...rest
  } = props;

  const options = propOptions || [];

  const classes = ['bubble-hintselect-container'];
  if (block) classes.push('full');
  if (propClassName) classes.push(propClassName);

  const menuClasses = ['bubble-hintselect-menu bg-white py-1 shadow rounded-br rounded-bl'];
  if (menuClassName) menuClasses.push(menuClassName);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputHasFocus, setInputHasFocus] = useState(false);

  function resolveLabel(val: string): string {
    if (labelExtractor) return labelExtractor(val);
    return val;
  }

  function resolveKey(val: string): string {
    if (keyExtractor) return keyExtractor(val);
    if (hasOwnProperty(val, 'key') && ['string', 'number'].includes(typeof val.key)) {
      return `${val.key}`;
    }
    return resolveLabel(val);
  }

  function onSelectItem(e: React.MouseEvent<HTMLButtonElement>, option: string) {
    e.preventDefault();
    e.stopPropagation();
    onValue?.(option);
    inputRef.current?.blur();
  }

  const filteredOptions = value
    ? options.filter(o => resolveLabel(o).toLowerCase().includes(value.toLowerCase()))
    : options;

  return (
    <div className={classes.join(' ')} style={propStyle}>
      {readOnly ? (
        <span>{value}</span>
      ) : (
        <div {...rest}>
          <BubbleInput
            variant={variant}
            inputRef={inputRef}
            type="text"
            block
            name={name}
            placeholder={placeholder}
            value={value}
            onValue={onValue}
            onFocus={() => setInputHasFocus(true)}
            onBlur={() => setInputHasFocus(false)}
            disabled={disabled}
            subtitle={subtitle}
          />
          {inputHasFocus && filteredOptions.length > 0 && (
            <div className={menuClasses.join(' ')}>
              {filteredOptions.map(o => (
                <BubbleButton
                  key={resolveKey(o)}
                  className="menu-button text-left font-normal-i"
                  block
                  variant="black"
                  inverse
                  allowWrap
                  onMouseDown={e => onSelectItem(e, o)}
                >
                  {resolveLabel(o)}
                </BubbleButton>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
